import { DATASET_SOURCES } from "../Dashboard/playerRoundVotes";

const normalize = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const ROUND_COUNT = 25; // rounds 0..24

/**
 * Per-round vote totals for a whole team, kept split by source: one entry per
 * enabled dataset, each with a dense rounds[0..24] array. Every vote the team's
 * players polled in a round is summed, so a round tops out at 6 (a 3-2-1 sweep).
 *
 * roster is the list of player objects the team table is aggregating. When it is
 * supplied only those players count, which keeps these charts in step with the
 * table's totals when players have been filtered out of the team.
 */
export const getTeamRoundsByDataset = (team, gameVoteData, enabledKeys = [], roster = null) => {
  if (!team || !gameVoteData) return [];

  const teamName = normalize(team);
  const rosterNames = Array.isArray(roster) && roster.length
    ? new Set(roster.map((p) => normalize(p?.name)))
    : null;

  const extract = (raw) => {
    const dataset = Array.isArray(raw) ? { game_vote_data: raw } : raw;
    const rounds = new Array(ROUND_COUNT).fill(0);
    (dataset?.game_vote_data || []).forEach((roundData) => {
      const round = Number(roundData.round);
      if (!Number.isInteger(round) || round < 0 || round >= ROUND_COUNT) return;
      (roundData.game_votes || []).forEach((game) => {
        (game.votes || []).forEach((vote) => {
          if (normalize(vote.team) !== teamName) return;
          if (rosterNames && !rosterNames.has(normalize(vote.player))) return;
          rounds[round] += Number(vote.votes) || 0;
        });
      });
    });
    return rounds;
  };

  const series = [];
  DATASET_SOURCES.forEach(({ key, label, field }) => {
    if (!enabledKeys.includes(key)) return;
    const raw = gameVoteData[field];
    if (!raw) return;
    series.push({ key, label, rounds: extract(raw) });
  });

  // Mirror the player helper's fallback to the combined dataset.
  if (series.length === 0 && gameVoteData.game_vote_data) {
    series.push({ key: 'combined', label: 'All', rounds: extract(gameVoteData.game_vote_data) });
  }

  return series;
};

/**
 * Per-round team vote totals averaged across the enabled datasets, as a dense
 * rounds[0..24] array. Derived from the per-source series so the breakdown and
 * the progression chart's consensus line can never drift apart.
 */
export const getTeamVotesByRound = (team, gameVoteData, enabledKeys = [], roster = null) => {
  const sources = getTeamRoundsByDataset(team, gameVoteData, enabledKeys, roster);
  if (!sources.length) return new Array(ROUND_COUNT).fill(0);

  return Array.from({ length: ROUND_COUNT }, (_, r) => {
    const sum = sources.reduce((acc, s) => acc + (Number(s.rounds[r]) || 0), 0);
    return sum / sources.length;
  });
};

export default getTeamVotesByRound;
